/* eslint-disable no-undef, no-new, no-unused-vars */
const EARTH_RADIUS_MILES = 3959
const MAP_BOUNDS_PADDING = { top: 20, right: 20, bottom: 20, left: 20 }
const INFO_WINDOW_WIDTH = 320
const WINDOW_OFFSET = 180
const ZOOM_INCREASE = 3
class ShowroomBooking extends HTMLElement {
  constructor () {
    super()
    this.map = null
    this.markers = new Map()
    this.showroomMarkers = []
    this.bounds = null
    this.closestShowroomsText = this.querySelector('.showroom-booking__closet-showrooms-text')
    this.showroomIcon = this.dataset.showroomIcon
    this.mapCenterCoordinates = this.dataset.mapCenterCoordinates?.split(',').map(coord => parseFloat(coord.trim())) || [53.36009499748501, -1.344126373082382]
    this.initialZoomLevel = parseInt(this.dataset.initialZoomLevel) || 5
    this.searchInputType = this.querySelector('input[name="location"]')?.getAttribute('autocomplete') === 'postal-code' ? 'postcode' : 'address'
    this.isEuStore = this.dataset.isEuStore === 'true'
    this.currentMarketHandle = this.dataset.currentMarketHandle || ''
    this.currentCountryCode = this.dataset.currentCountryCode || ''
    this.showAdditionalInfo = this.dataset.showAdditionalInfo === 'true'
    this.bookLabel = this.dataset.bookLabel || ''
    this.userChangedZoom = false
    this.hasZoomedToMarker = false
    this.resizeTimeout = null
    this.lastWindowWidth = window.innerWidth
    // Store EU store icons
    this.phoneIcon = this.dataset.phoneIcon || ''
    this.addressIcon = this.dataset.addressIcon || ''
    this.emailIcon = this.dataset.emailIcon || ''
    this.websiteIcon = this.dataset.websiteIcon || ''
    if (!this.showroomIcon.startsWith('http')) {
      this.showroomIcon = `https:${this.showroomIcon}`
    }
    this.handleScroll = this.handleScroll.bind(this)
    this.checkCalendlyLoaded()
  }

  checkCalendlyLoaded () {
    if (typeof Calendly !== 'undefined') {
      this.setupCalendlyButtons()
      return
    }
    const checkInterval = setInterval(() => {
      if (typeof Calendly !== 'undefined') {
        clearInterval(checkInterval)
        this.setupCalendlyButtons()
      }
    }, 100)
    setTimeout(() => {
      clearInterval(checkInterval)
      if (typeof Calendly === 'undefined') {
        this.handleCalendlyError()
      }
    }, 30000)
  }

  setupCalendlyButtons () {
    try {
      const bookNowButtons = this.querySelectorAll('.showroom-booking__locations-list--action-book-now')
      bookNowButtons.forEach(button => {
        const showroomId = button.closest('.showroom-booking__locations-list--item')?.dataset.showroomId
        if (!showroomId) return
        const showroom = this.findShowroomByIdentifier(showroomId)
        if (!showroom?.calendly_link) return
        button.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          this.openCalendlyWidgetForShowroom(showroom)
        }
      })
      const listItems = this.querySelectorAll('.showroom-booking__locations-list--item')
      listItems.forEach(item => {
        const showroomId = item.dataset.showroomId
        if (!showroomId) return
        const showroom = this.findShowroomByIdentifier(showroomId)
        if (!showroom?.calendly_link) return
        item.style.cursor = 'pointer'
        item.addEventListener('click', (e) => {
          if (e.target.closest('button') || e.target.closest('a')) return
          this.openCalendlyWidgetForShowroom(showroom)
        })
      })
    } catch (error) {
      this.handleCalendlyError()
    }
  }

  openCalendlyWidgetForShowroom (showroom) {
    try {
      if (typeof Calendly === 'undefined') {
        this.handleCalendlyError()
        return
      }

      // Get the correct Calendly URL regardless of property naming
      const calendlyUrl = showroom.calendlyLink || showroom.calendly_link

      if (!calendlyUrl) {
        return
      }

      Calendly.initPopupWidget({
        url: `${calendlyUrl}?hide_gdpr_banner=1&primary_color=ed8008`,
      })
    } catch (error) {
      this.handleCalendlyError()
    }
  }

  handleCalendlyError () {
    const bookNowButtons = this.querySelectorAll('.showroom-booking__locations-list--action-book-now')
    bookNowButtons.forEach(button => {
      button.classList.add('opacity-50', 'cursor-not-allowed')
      button.disabled = true
      const originalText = button.textContent
      button.innerHTML = `
        <span class="w-5 h-5 mr-2 inline-flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </span>
        Booking temporarily unavailable
      `
    })
  }

  connectedCallback () {
    this.debugShowroomData()
    this.initializeElements()
    this.initializeWithSortedShowrooms()
    this.addEventListeners()
    this.initializeMobileNav()
    if (this.mapContainer) {
      this.initializeMapWithUKBounds(window.showroomLocations || [])
    }
    window.addEventListener('resize', () => {
      const currentWidth = window.innerWidth
      if (currentWidth === this.lastWindowWidth) {
        return
      }
      this.lastWindowWidth = currentWidth
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
      }
      this.resizeTimeout = setTimeout(() => {
        this.handleResponsiveLayout()
      }, 250)
    })
  }

  initializeElements () {
    this.postcodeForm = this.querySelector('.showroom-booking__postcode-form')
    this.postcodeInput = this.querySelector('.showroom-booking__postcode-input input')
    this.locationsContainer = this.querySelector('.showroom-booking__locations')
    this.mapContainer = this.querySelector('.showroom-booking__locations-map')
    this.listViewButton = this.querySelector('.showroom-booking__nav--list-view')
    this.mapViewButton = this.querySelector('.showroom-booking__nav--map-view')
    this.listViewColumn = this.querySelector('.showroom-booking__locations-column--list')
    this.mapViewColumn = this.querySelector('.showroom-booking__locations-column--map')
    if (this.searchInputType === 'postcode') {
      this.postcodeInput.addEventListener('input', (event) => {
        const start = event.target.selectionStart
        const end = event.target.selectionEnd
        event.target.value = event.target.value.toUpperCase()
        event.target.setSelectionRange(start, end)
        this.toggleClearButton()
      })
    } else {
      this.postcodeInput.addEventListener('input', () => {
        this.toggleClearButton()
      })
    }
    this.listWrapper = this.querySelector('.showroom-booking__locations-list-wrapper')
    this.listFader = this.querySelector('.showroom-booking__locations-list-fader')
    this.listFaderTop = this.querySelector('.showroom-booking__locations-list-fader-top')
    if (this.listWrapper && this.listFader) {
      this.listWrapper.addEventListener('scroll', this.handleScroll)
      this.updateFaderVisibility()
    }
    this.clearButton = this.querySelector('.showroom-booking__postcode-clear')
  }

  initializeMobileNav () {
    if (window.innerWidth < 1280) {
      this.listViewColumn.classList.remove('hidden')
      this.mapViewColumn.classList.add('hidden')
      this.listViewButton.classList.add('tb-timeline-link-active')
      this.mapViewButton.classList.remove('tb-timeline-link-active')
    }
  }

  addEventListeners () {
    this.postcodeForm.addEventListener('submit', (event) => {
      event.preventDefault()
      this.handlePostcodeSubmit()
    })
    if (typeof Calendly !== 'undefined') {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          Calendly.closePopupWidget()
        }
      })
    }
    this.listViewButton.addEventListener('click', () => {
      this.switchToView('list')
    })
    this.mapViewButton.addEventListener('click', () => {
      this.switchToView('map')
    })
    window.addEventListener('resize', () => {
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
        this.fitMapBounds()
      }
    })
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.resetTool()
      })
    }
  }

  switchToView (view) {
    if (window.innerWidth < 1280) {
      this.currentView = view
      this.toggleViewColumns(view === 'list')
      if (view === 'map' && this.map) {
        google.maps.event.trigger(this.map, 'resize')
        if (this.searchResults && this.searchResults.length > 0) {
          this.zoomToClosestShowrooms(this.searchResults.slice(0, 3))
        } else if (this.isCountrySpecificMarket()) {
          const countryBounds = this.getCountryBounds(this.currentCountryCode)
          if (countryBounds) {
            this.map.fitBounds(countryBounds, MAP_BOUNDS_PADDING)
            setTimeout(() => {
              const currentZoom = this.map.getZoom()
              if (currentZoom > 7) {
                this.map.setZoom(7)
              }
            }, 100)
          }
        } else {
          this.fitMapBounds()
        }
      }
    }
  }

  toggleViewColumns (showList) {
    if (window.innerWidth < 1280) {
      this.listViewColumn.classList.toggle('hidden', !showList)
      this.mapViewColumn.classList.toggle('hidden', showList)
      this.listViewButton.classList.toggle('tb-timeline-link-active', showList)
      this.mapViewButton.classList.toggle('tb-timeline-link-active', !showList)
    }
  }

  async handlePostcodeSubmit () {
    if (this.loading) return
    this.loading = true
    const startTime = Date.now()
    const location = this.postcodeInput.value.trim()

    this.setAttribute('loading', '')
    if (!location) {
      await this.ensureMinimumLoadingTime(startTime)
      this.loading = false
      this.removeAttribute('loading')
      return
    }
    try {
      const locationData = await this.getLocationCoordinates(location)
      if (!locationData) throw new Error('Invalid location')

      this.showAllMarkers()
      const nearestShowrooms = this.findNearestShowrooms(locationData)

      if (locationData.isCountrySearch) {
        this.showroomMarkers.forEach(marker => {
          const showroomId = marker.get('showroomId')
          const showroom = window.showroomLocations.find(s => String(s.id) === String(showroomId))
          if (showroom) {
            const showroomCountryCode = this.getCountryCodeFromRegion(showroom.region_handle)
            marker.setMap(showroomCountryCode === locationData.countryCode ? this.map : null)
          }
        })
      } else {
        this.showroomMarkers.forEach(marker => marker.setMap(this.map))
      }
      this.searchResults = nearestShowrooms
      if (this.closestShowroomsText) {
        this.closestShowroomsText.innerHTML = this.closestShowroomsText.innerHTML.replace('{{ postcode }}', `<strong>${location}</strong>`)
        this.closestShowroomsText.classList.remove('hidden')
      }
      this.initializeMap(locationData, nearestShowrooms)
      await this.ensureMinimumLoadingTime(startTime)

      this.showResults(nearestShowrooms)

      if (locationData.isCountrySearch && locationData.viewport) {
        this.map.fitBounds(locationData.viewport, MAP_BOUNDS_PADDING)
      } else {
        this.zoomToClosestShowrooms(nearestShowrooms.slice(0, 3))
      }
      if (this.locationsContainer && this.querySelector('.showroom-booking__postcode')) {
        this.locationsContainer.classList.remove('hidden')
        const postcodeElement = this.querySelector('.showroom-booking__postcode')
        postcodeElement.classList.remove('mb-[250px]')
      }
      this.loading = false
      this.removeAttribute('loading')
    } catch (error) {
      await this.ensureMinimumLoadingTime(startTime)
      this.loading = false
      this.removeAttribute('loading')
    }
  }

  async ensureMinimumLoadingTime (startTime) {
    const elapsedTime = Date.now() - startTime
    const remainingTime = Math.max(0, 800 - elapsedTime)
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime))
    }
  }

  async getLocationCoordinates (location) {
    const geocoder = new google.maps.Geocoder()
    try {
      let searchQuery = location
      let isCountrySearch = false
      const countryCode = this.getCountryCodeFromRegion(location)
      if (countryCode) {
        isCountrySearch = true
        searchQuery = location
      } else {
        const firstShowroom = window.showroomLocations?.[0]
        if (firstShowroom?.region_handle) {
          const countryCode = this.getCountryCodeFromRegion(firstShowroom.region_handle)
          if (countryCode) {
            if (this.searchInputType === 'postcode') {
              searchQuery = `${location}, ${countryCode}`
            } else {
              if (!location.toLowerCase().includes(countryCode.toLowerCase())) {
                searchQuery = `${location}, ${countryCode}`
              }
            }
          }
        }
      }
      const response = await geocoder.geocode({
        address: searchQuery,
        region: isCountrySearch ? countryCode : this.getCountryCodeFromRegion(window.showroomLocations?.[0]?.region_handle) || undefined,
      })
      if (response.results && response.results[0]) {
        const { lat, lng } = response.results[0].geometry.location
        return {
          lat: lat(),
          lng: lng(),
          isCountrySearch,
          countryCode: isCountrySearch ? countryCode : null,
          viewport: response.results[0].geometry.viewport,
        }
      }
      return null
    } catch (error) {
      return null
    }
  }

  getCountryCodeFromRegion (region) {
    // Accept both handles (lowercase slugs) and English names for backward compatibility
    const regionToCountry = {
      // Handles (lowercase slugs)
      england: 'GB',
      scotland: 'GB',
      wales: 'GB',
      'northern-ireland': 'GB',
      france: 'FR',
      germany: 'DE',
      italy: 'IT',
      spain: 'ES',
      netherlands: 'NL',
      belgium: 'BE',
      austria: 'AT',
      switzerland: 'CH',
      denmark: 'DK',
      sweden: 'SE',
      norway: 'NO',
      finland: 'FI',
      ireland: 'IE',
      portugal: 'PT',
      greece: 'GR',
      poland: 'PL',
      'czech-republic': 'CZ',
      slovakia: 'SK',
      hungary: 'HU',
      romania: 'RO',
      bulgaria: 'BG',
      croatia: 'HR',
      slovenia: 'SI',
      estonia: 'EE',
      latvia: 'LV',
      lithuania: 'LT',
      cyprus: 'CY',
      malta: 'MT',
      luxembourg: 'LU',
      iceland: 'IS',
      liechtenstein: 'LI',
      // English names for backward compatibility
      England: 'GB',
      Scotland: 'GB',
      Wales: 'GB',
      'Northern Ireland': 'GB',
      France: 'FR',
      Germany: 'DE',
      Italy: 'IT',
      Spain: 'ES',
      Netherlands: 'NL',
      Belgium: 'BE',
      Austria: 'AT',
      Switzerland: 'CH',
      Denmark: 'DK',
      Sweden: 'SE',
      Norway: 'NO',
      Finland: 'FI',
      Ireland: 'IE',
      Portugal: 'PT',
      Greece: 'GR',
      Poland: 'PL',
      'Czech Republic': 'CZ',
      Slovakia: 'SK',
      Hungary: 'HU',
      Romania: 'RO',
      Bulgaria: 'BG',
      Croatia: 'HR',
      Slovenia: 'SI',
      Estonia: 'EE',
      Latvia: 'LV',
      Lithuania: 'LT',
      Cyprus: 'CY',
      Malta: 'MT',
      Luxembourg: 'LU',
      Iceland: 'IS',
      Liechtenstein: 'LI',
    }
    return regionToCountry[region] || null
  }

  isCountrySpecificMarket () {
    // Only apply country-specific behavior for FR, DE, and IT markets
    // The default EU store should use alphabetical ordering and default centering
    return this.isEuStore && this.currentCountryCode && ['FR', 'DE', 'IT'].includes(this.currentCountryCode)
  }

  getCountryBounds (countryCode) {
    const countryBounds = {
      GB: { north: 58.6350001, south: 50.0000000, west: -10.5500000, east: 1.7800000 },
      FR: { north: 51.1242, south: 41.3253, west: -5.5591, east: 9.6625 },
      DE: { north: 55.0815, south: 47.2701, west: 5.8663, east: 15.0419 },
      IT: { north: 47.0921, south: 35.4929, west: 6.6267, east: 18.7975 },
      ES: { north: 43.7913, south: 35.1736, west: -9.3014, east: 4.3278 },
      NL: { north: 53.6756, south: 50.7504, west: 3.3316, east: 7.2275 },
      BE: { north: 51.5051, south: 49.4969, west: 2.5465, east: 6.4078 },
      AT: { north: 49.0211, south: 46.3722, west: 9.5307, east: 17.1608 },
      CH: { north: 47.8084, south: 45.8179, west: 5.9559, east: 10.4922 },
      DK: { north: 57.7513, south: 54.5594, west: 8.0756, east: 15.1588 },
      SE: { north: 69.0599, south: 55.3617, west: 11.0273, east: 24.1773 },
      NO: { north: 71.1853, south: 57.9594, west: 4.6065, east: 31.2935 },
      FI: { north: 70.0922, south: 59.8084, west: 20.5418, east: 31.5867 },
      IE: { north: 55.4360, south: 51.4166, west: -10.4800, east: -5.9917 },
      PT: { north: 42.1543, south: 36.9382, west: -9.5261, east: -6.1891 },
      GR: { north: 41.7488, south: 34.8021, web: 19.3736, east: 28.2336 },
      PL: { north: 54.8356, south: 49.0020, west: 14.1229, east: 24.1458 },
    }
    return countryBounds[countryCode] || null
  }

  findNearestShowrooms (postcodeCoords) {
    const allLocations = window.showroomLocations || []

    const showroomsWithDistance = allLocations.map(location => {
      const distance = this.calculateDistance(postcodeCoords, {
        lat: parseFloat(location.pin_lat),
        lng: parseFloat(location.pin_long),
      })

      return {
        ...location,
        pinLat: parseFloat(location.pin_lat),
        pinLong: parseFloat(location.pin_long),
        distance,
      }
    })

    const sortedShowrooms = showroomsWithDistance.sort((a, b) => a.distance - b.distance)

    return sortedShowrooms
  }

  initializeMap (center, showrooms) {
    const currentZoom = this.map ? this.map.getZoom() : 8
    if (!this.map) {
      this.map = new google.maps.Map(this.mapContainer, {
        zoom: 5,
        center,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: [],
        gestureHandling: 'cooperative',
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        scaleControl: true,
        rotateControl: false,
        disableDefaultUI: false,
      })
      this.map.addListener('zoom_changed', () => {
        this.currentZoom = this.map.getZoom()
        this.userChangedZoom = true
      })
      this.map.addListener('click', () => {
        if (this.currentInfoWindow) {
          this.currentInfoWindow.setMap(null)
        }
      })
    }
    this.markers.forEach(marker => marker.setMap(null))
    this.markers.clear()
    this.showroomMarkers = []
    if (this.currentInfoWindow) {
      this.currentInfoWindow.setMap(null)
    }
    this.bounds = new google.maps.LatLngBounds()
    const bookAppointmentLabel = this.getAttribute('data-book-appointment-label')
    const bookNowLabel = this.getAttribute('data-book-now-label')
    showrooms.forEach((showroom, index) => {
      const position = {
        lat: parseFloat(showroom.pin_lat),
        lng: parseFloat(showroom.pin_long),
      }
      if (isNaN(position.lat) || isNaN(position.lng)) return
      const marker = this.createMarker(position, showroom, showroom.id, index)
      marker.setMap(this.map)
      marker.set('showroomId', String(showroom.id))
      this.markers.set(showroom.id, marker)
      this.showroomMarkers.push(marker)
      this.bounds.extend(position)
    })
  }

  createMarker (position, showroom, showroomId, index) {
    const marker = new google.maps.Marker({
      position,
      map: null,
      icon: {
        url: this.dataset.mapMarker,
        scaledSize: new google.maps.Size(40, 49),
        anchor: new google.maps.Point(20, 49),
      },
    })
    marker.set('showroomId', showroomId)
    marker.set('regionName', showroom.region_name || 'No Region')
    marker.set('index', index)
    marker.addListener('click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.setMap(null)
      }
      const InfoWindowClass = this.createCustomInfoWindow(position, showroom)
      this.currentInfoWindow = new InfoWindowClass(this.decodeHtmlEntities, this.getCountryFlag.bind(this), this.bookLabel)
      this.currentInfoWindow.setMap(this.map)

      // Position the marker lower in viewport to ensure info window is fully visible
      this.panToShowInfoWindow(position)
    })
    return marker
  }

  fitMapBounds () {
    if (this.bounds && this.map) {
      if (!this.hasZoomedToMarker) {
        this.map.fitBounds(this.bounds, MAP_BOUNDS_PADDING)
        setTimeout(() => {
          const zoom = this.map.getZoom()
          if (zoom && zoom > 12) {
            this.map.setZoom(12)
          }
        }, 100)
      }
    }
  }

  calculateDistance (point1, point2) {
    const lat1 = this.toRadians(point1.lat)
    const lat2 = this.toRadians(point2.lat)
    const dLat = this.toRadians(point2.lat - point1.lat)
    const dLon = this.toRadians(point2.lng - point1.lng)
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return Math.round(EARTH_RADIUS_MILES * c)
  }

  toRadians (degrees) {
    return degrees * (Math.PI / 180)
  }

  showResults (nearestShowrooms) {
    const listContainer = this.querySelector('.showroom-booking__locations-list')
    if (!listContainer) return

    this.showroomMarkers.forEach(marker => {
      const showroomId = marker.get('showroomId')
      const isInResults = nearestShowrooms.some(showroom => String(showroom.id) === String(showroomId))
      marker.setMap(isInResults ? this.map : null)
    })
    const locationItems = listContainer.querySelectorAll('.showroom-booking__locations-list--item')

    locationItems.forEach((item, index) => {
      const showroom = nearestShowrooms[index]

      // Ensure showroom data integrity
      const cleanShowroom = showroom ? this.ensureShowroomDataIntegrity(showroom) : null

      if (cleanShowroom) {
        item.style.display = ''

        // Store the clean showroom data on the element for later use
        item._showroomData = cleanShowroom

        const locationText = item.querySelector('.showroom-booking__locations-list--location')
        if (locationText) {
          const displayName = this.decodeHtmlEntities(cleanShowroom.closestCity || cleanShowroom.town || cleanShowroom.companyName)
          const shouldShowFlag = this.isEuStore
          const flag = shouldShowFlag ? this.getCountryFlag(cleanShowroom.regionHandle) : ''
          const countryCode = shouldShowFlag ? this.getCountryCodeFromRegion(cleanShowroom.regionHandle) : ''
          const countryName = countryCode && window.Tentbox?.translations?.countries?.[countryCode.toLowerCase()] ? window.Tentbox.translations.countries[countryCode.toLowerCase()] : ''
          locationText.innerHTML = `${displayName} Showroom${flag ? `<div class="text-base font-normal mt-0.5">${flag} ${countryName}</div>` : ''}`
        }
        const distanceText = item.querySelector('.showroom-booking__locations-column--distance')
        if (distanceText) {
          distanceText.textContent = `${cleanShowroom.distance} miles`
        }

        // Update contact information if additional info is enabled
        if (this.showAdditionalInfo) {
          this.updateContactInfo(item, cleanShowroom, index)
        }

        // Clear any existing event listeners by cloning the item
        const newItem = item.cloneNode(true)
        item.parentNode.replaceChild(newItem, item)

        // Re-store the data on the new element
        newItem._showroomData = cleanShowroom

        // Set up Calendly functionality with clean data
        if (cleanShowroom.calendlyLink) {
          newItem.style.cursor = 'pointer'
          const displayName = this.decodeHtmlEntities(cleanShowroom.closestCity || cleanShowroom.town || cleanShowroom.companyName)
          newItem.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)

          // Card click handler
          newItem.onclick = (e) => {
            if (e.target.closest('button') || e.target.closest('a')) return
            this.openCalendlyWidgetForShowroom(cleanShowroom)
          }

          // Book now button click handler
          const bookNowButton = newItem.querySelector('.showroom-booking__locations-list--action-book-now')
          if (bookNowButton) {
            bookNowButton.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)
            bookNowButton.onclick = (e) => {
              e.preventDefault()
              e.stopPropagation()
              this.openCalendlyWidgetForShowroom(cleanShowroom)
              return false
            }
          }
        }
      } else {
        item.style.display = 'none'
      }
    })
  }

  updateContactInfo (item, showroom, cardIndex) {
    // Update address
    const addressElement = item.querySelector('.showroom-booking__contact-info--address-text')
    if (addressElement) {
      const addressParts = []
      if (showroom.streetAddress) addressParts.push(this.decodeHtmlEntities(showroom.streetAddress))
      if (showroom.town) addressParts.push(this.decodeHtmlEntities(showroom.town))
      if (showroom.postcode) addressParts.push(this.decodeHtmlEntities(showroom.postcode))
      const fullAddress = addressParts.join(', ') || 'No address available'
      addressElement.textContent = fullAddress
    }

    // Update phone
    const phoneElement = item.querySelector('.showroom-booking__contact-info--phone')
    if (phoneElement && showroom.phone) {
      phoneElement.href = `tel:${showroom.phone}`
      phoneElement.textContent = showroom.phone
      phoneElement.style.display = ''
      phoneElement.parentElement.style.display = ''
    } else if (phoneElement && !showroom.phone) {
      phoneElement.parentElement.style.display = 'none'
    }

    // Update email
    const emailElement = item.querySelector('.showroom-booking__contact-info--email')
    if (emailElement && showroom.email) {
      const decodedEmail = this.decodeHtmlEntities(showroom.email)
      emailElement.href = `mailto:${decodedEmail}`
      emailElement.textContent = decodedEmail
      emailElement.style.display = ''
      emailElement.parentElement.style.display = ''
    } else if (emailElement && !showroom.email) {
      emailElement.parentElement.style.display = 'none'
    }

    // Update website
    const websiteElement = item.querySelector('.showroom-booking__contact-info--website')
    if (websiteElement && showroom.website) {
      const decodedWebsite = this.decodeHtmlEntities(showroom.website)
      const decodedWebsitePretty = this.decodeHtmlEntities(showroom.websitePretty || showroom.website)
      websiteElement.href = decodedWebsite
      websiteElement.textContent = decodedWebsitePretty
      websiteElement.style.display = ''
      websiteElement.parentElement.style.display = ''
    } else if (websiteElement && !showroom.website) {
      websiteElement.parentElement.style.display = 'none'
    }
  }

  logCurrentCardStates () {
    // This method is kept for potential future debugging but no longer logs
  }

  set loading (value) {
    this.postcodeForm.setAttribute('loading', value)

    // Also apply loading state to contact information elements
    if (value === 'true') {
      this.setAttribute('loading', '')
    } else {
      this.removeAttribute('loading')
    }
  }

  get loading () {
    return this.postcodeForm.getAttribute('loading') === 'true'
  }

  updateLocationsList () {
    const decoder = document.createElement('div')
    const locationItems = this.querySelectorAll('.showroom-booking__locations-list--item')
    const visibleShowrooms = []
    let visibleItemCount = 0
    locationItems.forEach(item => {
      decoder.innerHTML = item.dataset.regionName
      const showroomId = item.dataset.showroomId
      if (isVisible) {
        item.classList.remove('hidden')
        visibleItemCount++
        const showroom = this.findShowroomByIdentifier(showroomId)
        if (showroom) {
          visibleShowrooms.push(showroom)
        }
      } else {
        item.classList.add('hidden')
      }
    })
    if (!this.searchResults) {
      this.resetMapToUK()
    }
  }

  findShowroomByIdentifier (identifier) {
    if (!window.showroomLocations) return null
    if (this.showroomIdProperty) {
      const showroom = window.showroomLocations.find(d =>
        String(d[this.showroomIdProperty]) === String(identifier),
      )
      if (showroom) return showroom
    }
    if (identifier && identifier.startsWith('showroom-')) {
      const index = parseInt(identifier.split('-')[1], 10)
      if (!isNaN(index) && index < window.showroomLocations.length) {
        return window.showroomLocations[index]
      }
    }
    return null
  }

  resetMapToUK () {
    if (!this.map) return
    let bounds = {
      north: 58.6350001,
      south: 50.0000000,
      west: -10.5500000,
      east: 1.7800000,
    }
    if (this.isCountrySpecificMarket()) {
      const countryBounds = this.getCountryBounds(this.currentCountryCode)
      if (countryBounds) {
        bounds = countryBounds
      }
    }
    this.map.fitBounds(bounds)
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      const maxZoom = this.isCountrySpecificMarket() ? 7 : 6
      if (this.map.getZoom() > maxZoom) return
      this.map.setZoom(Math.min(this.map.getZoom() + 1, maxZoom))
    })
  }

  initializeWithSortedShowrooms () {
    const allShowrooms = window.showroomLocations || []

    let sortedShowrooms

    // Apply market-prioritized sorting for FR and DE markets
    if (this.currentCountryCode === 'FR' || this.currentCountryCode === 'DE') {
      const targetRegionHandle = this.currentCountryCode === 'FR' ? 'france' : 'germany'

      const marketShowrooms = []
      const otherShowrooms = []

      allShowrooms.forEach(showroom => {
        if (showroom.region_handle === targetRegionHandle) {
          marketShowrooms.push(showroom)
        } else {
          otherShowrooms.push(showroom)
        }
      })

      // Sort each group alphabetically
      const sortAlphabetically = (a, b) => {
        const nameA = a.closest_city || a.town || a.company_name
        const nameB = b.closest_city || b.town || b.company_name
        return nameA.localeCompare(nameB)
      }

      marketShowrooms.sort(sortAlphabetically)
      otherShowrooms.sort(sortAlphabetically)

      sortedShowrooms = [...marketShowrooms, ...otherShowrooms]
    } else {
      // Default alphabetical sorting for all other markets
      sortedShowrooms = allShowrooms.sort((a, b) => {
        const nameA = a.closest_city || a.town || a.company_name
        const nameB = b.closest_city || b.town || b.company_name
        return nameA.localeCompare(nameB)
      })
    }

    if (!this.showroomIdProperty) {
      const firstShowroom = sortedShowrooms[0]
      if (firstShowroom) {
        this.showroomIdProperty = 'company_name'
      }
    }
    const locationItems = this.querySelectorAll('.showroom-booking__locations-list--item')

    locationItems.forEach((item, index) => {
      const showroom = sortedShowrooms[index]
      if (!showroom) {
        item.classList.add('hidden')
        return
      }
      const showroomId = showroom[this.showroomIdProperty] || `showroom-${index}`
      item.dataset.showroomId = showroomId
      item.dataset.region = showroom.region || ''
      const regionName = showroom.region_name || 'No Region'
      item.dataset.regionName = regionName

      const locationContainer = item.querySelector('.showroom-booking__locations-list--location')
      if (locationContainer) {
        const displayName = this.decodeHtmlEntities(showroom.closest_city || showroom.town || showroom.company_name)
        const shouldShowFlag = this.isEuStore
        const flag = shouldShowFlag ? this.getCountryFlag(showroom.region_handle) : ''
        const countryCode = shouldShowFlag ? this.getCountryCodeFromRegion(showroom.region_handle) : ''
        const countryName = countryCode && window.Tentbox?.translations?.countries?.[countryCode.toLowerCase()] ? window.Tentbox.translations.countries[countryCode.toLowerCase()] : ''
        locationContainer.innerHTML = `${displayName} Showroom${flag ? `<div class="text-base font-normal mt-0.5">${flag} ${countryName}</div>` : ''}`
      }

      // Initialize contact info with actual data
      if (this.showAdditionalInfo) {
        const cleanShowroom = this.ensureShowroomDataIntegrity(showroom)
        this.updateContactInfo(item, cleanShowroom, index)
      }

      if (showroom.calendly_link) {
        item.style.cursor = 'pointer'
        const displayName = this.decodeHtmlEntities(showroom.closest_city || showroom.town || showroom.company_name)
        item.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)
        item.onclick = (e) => {
          if (e.target.closest('button') || e.target.closest('a')) return
          this.openCalendlyWidgetForShowroom(showroom)
        }
      }
      if (typeof Calendly !== 'undefined') {
        const calenderLinkButton = item.querySelector('.showroom-booking__locations-list--action-book-now')
        if (calenderLinkButton && showroom.calendly_link) {
          const displayName = this.decodeHtmlEntities(showroom.closest_city || showroom.town || showroom.company_name)
          calenderLinkButton.setAttribute('data-tracking', `showroom-locations-booking_${displayName}`)
          calenderLinkButton.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            this.openCalendlyWidgetForShowroom(showroom)
            return false
          })
        }
      }
    })
  }

  initializeMapWithUKBounds (showrooms) {
    const mapCenter = { lat: this.mapCenterCoordinates[0], lng: this.mapCenterCoordinates[1] }
    const mapZoom = this.initialZoomLevel

    this.map = new google.maps.Map(this.mapContainer, {
      center: mapCenter,
      zoom: mapZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      scaleControl: true,
      zoomControl: true,
      rotateControl: false,
      disableDefaultUI: false,
    })
    this.map.addListener('zoom_changed', () => {
      this.currentZoom = this.map.getZoom()
      this.userChangedZoom = true
    })
    this.map.addListener('click', () => {
      if (this.currentInfoWindow) {
        this.currentInfoWindow.setMap(null)
      }
    })
    this.hasZoomedToMarker = false
    if (this.showroomMarkers && this.showroomMarkers.length) {
      this.showroomMarkers.forEach(marker => marker.setMap(null))
    }
    this.showroomMarkers = []
    this.markers = new Map()
    showrooms.forEach((showroom, index) => {
      if (showroom.pin_lat && showroom.pin_long) {
        const position = {
          lat: parseFloat(showroom.pin_lat),
          lng: parseFloat(showroom.pin_long),
        }
        if (isNaN(position.lat) || isNaN(position.lng)) return
        const showroomId = showroom[this.showroomIdProperty] || `showroom-${index}`
        const marker = this.createMarker(position, showroom, showroomId, index)
        marker.setMap(this.map)
        this.showroomMarkers.push(marker)
        this.markers.set(showroomId, marker)
      }
    })
  }

  handleScroll () {
    this.updateFaderVisibility()
  }

  updateFaderVisibility () {
    if (!this.listWrapper || !this.listFader || !this.listFaderTop || window.innerWidth < 1280) return
    const isScrollable = this.listWrapper.scrollHeight > this.listWrapper.clientHeight
    const isAtBottom = Math.ceil(this.listWrapper.scrollTop + this.listWrapper.clientHeight) >= this.listWrapper.scrollHeight
    const isAtTop = this.listWrapper.scrollTop <= 0
    if (!isScrollable || isAtBottom) {
      this.listFader.style.opacity = '0'
    } else {
      this.listFader.style.opacity = '1'
    }
    if (!isScrollable || isAtTop) {
      this.listFaderTop.style.opacity = '0'
    } else {
      this.listFaderTop.style.opacity = '1'
    }
  }

  handleResponsiveLayout () {
    const isDesktop = window.innerWidth >= 1280
    if (isDesktop) {
      this.listViewColumn.classList.remove('hidden')
      this.mapViewColumn.classList.remove('hidden')
      if (this.map) {
        google.maps.event.trigger(this.map, 'resize')
        this.fitMapBounds()
      }
    } else {
      const isListVisible = !this.listViewColumn.classList.contains('hidden')
      const isMapVisible = !this.mapViewColumn.classList.contains('hidden')
      if (isMapVisible || !isListVisible) {
        this.initializeMobileNav()
      }
    }
  }

  disconnectedCallback () {
    if (this.listWrapper) {
      this.listWrapper.removeEventListener('scroll', this.handleScroll)
    }
  }

  createCustomInfoWindow (position, showroom) {
    class CustomInfoWindow extends google.maps.OverlayView {
      constructor (decodeHtmlEntities, getCountryFlag, bookLabel) {
        super()
        this.position = position
        this.showroom = showroom
        this.decodeHtmlEntities = decodeHtmlEntities
        this.getCountryFlag = getCountryFlag
        this.bookLabel = bookLabel
      }

      onAdd () {
        const div = document.createElement('div')
        div.className = 'showroom-booking__info-window-container'
        div.style.position = 'absolute'
        div.style.zIndex = '1000'

        const pointer = document.createElement('div')
        pointer.className = 'showroom-booking__info-window--pointer absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45'

        const bookNowLabel = document.querySelector('showroom-booking').getAttribute('data-book-now-label')
        const rawDisplayName = this.showroom.closest_city || this.showroom.town || this.showroom.company_name
        const displayName = this.decodeHtmlEntities(rawDisplayName)

        // Get the showroom booking element to access settings and icons
        const showroomBookingElement = document.querySelector('showroom-booking')
        const showAdditionalInfo = showroomBookingElement?.dataset.showAdditionalInfo === 'true'

        // Build contact info
        let contactInfoHtml = ''
        if (showAdditionalInfo) {
          const phoneIcon = showroomBookingElement?.dataset.phoneIcon || ''
          const addressIcon = showroomBookingElement?.dataset.addressIcon || ''
          const emailIcon = showroomBookingElement?.dataset.emailIcon || ''
          const websiteIcon = showroomBookingElement?.dataset.websiteIcon || ''

          contactInfoHtml = '<div class="flex flex-col gap-2 mt-2 pt-4">'

          // Address
          if (this.showroom.street_address || this.showroom.town || this.showroom.postcode) {
            const addressParts = []
            if (this.showroom.street_address) addressParts.push(this.decodeHtmlEntities(this.showroom.street_address))
            if (this.showroom.town) addressParts.push(this.decodeHtmlEntities(this.showroom.town))
            if (this.showroom.postcode) addressParts.push(this.decodeHtmlEntities(this.showroom.postcode))
            const fullAddress = addressParts.join(', ')
            const copyIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z" /></svg>`
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${addressIcon}</div>
                <div class="flex-grow flex justify-between items-start gap-2 min-w-0">
                  <span class="text-sm break-words flex-grow">${fullAddress}</span>
                  <button class="showroom-booking__copy-address w-4 h-4 shrink-0 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer" 
                          data-address="${fullAddress.replace(/"/g, '&quot;')}" 
                          title="Copy address">
                    ${copyIcon}
                  </button>
                </div>
              </div>
            `
          }

          // Phone
          if (this.showroom.phone) {
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${phoneIcon}</div>
                <div class="flex-grow min-w-0">
                  <a href="tel:${this.showroom.phone}" class="text-sm underline underline-offset-2 hover:no-underline break-all">${this.showroom.phone}</a>
                </div>
              </div>
            `
          }

          // Email
          if (this.showroom.email) {
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${emailIcon}</div>
                <div class="flex-grow min-w-0">
                  <a href="mailto:${this.showroom.email}" class="text-sm underline underline-offset-2 hover:no-underline break-all">${this.decodeHtmlEntities(this.showroom.email)}</a>
                </div>
              </div>
            `
          }

          // Website
          if (this.showroom.website) {
            contactInfoHtml += `
              <div class="flex gap-2">
                <div class="w-5 h-5 shrink-0">${websiteIcon}</div>
                <div class="flex-grow min-w-0">
                  <a href="${this.showroom.website}" target="_blank" rel="noopener noreferrer" class="text-sm underline underline-offset-2 hover:no-underline break-all">
                    ${this.decodeHtmlEntities(this.showroom.website_pretty || this.showroom.website)}
                  </a>
                </div>
              </div>
            `
          }

          contactInfoHtml += '</div>'
        }

        const content = document.createElement('div')
        content.className = 'showroom-booking__info-window relative p-5 bg-white rounded-lg shadow-lg min-w-[280px]'

        // Set maximum width based on map container width
        const mapDiv = this.getMap().getDiv()
        const mapWidth = mapDiv.offsetWidth
        const maxWidth = Math.min(320, mapWidth - 40) // Max 320px or map width minus 40px padding
        content.style.maxWidth = `${maxWidth}px`
        content.style.width = 'auto'
        content.innerHTML = `
            <button class="showroom-booking__info-window--close absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div class="flex flex-col overflow-hidden">
              <div class="text-xl font-semibold pr-8 break-words">${displayName} Showroom</div>
              ${contactInfoHtml}
              <div class="mt-5">
                ${this.bookLabel ? `<div class="showroom-booking__info-window--book-now-label text-base mb-2 break-words">${this.bookLabel}</div>` : ''}
                <button class="showroom-booking__info-window--book-now button button-orange h-10 w-full text-sm min-w-0">
                  <span class="w-5 h-5 mr-2 inline-flex items-center justify-center shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                      </svg>
                  </span>
                  <span class="truncate">${bookNowLabel}</span>
                </button>
              </div>
            </div>
          `

        // Add click handler for the close button
        const closeButton = content.querySelector('.showroom-booking__info-window--close')
        const handleClose = (e) => {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          this.setMap(null)
          return false
        }
        closeButton.addEventListener('click', handleClose, { passive: false })
        closeButton.addEventListener('touchend', handleClose, { passive: false })

        // Add click handler for the book now button
        const bookNowButton = content.querySelector('.showroom-booking__info-window--book-now')
        if (bookNowButton && this.showroom.calendly_link) {
          const handleBooking = (e) => {
            e.preventDefault()
            e.stopPropagation()
            e.stopImmediatePropagation()

            try {
              Calendly.initPopupWidget({
                url: `${this.showroom.calendly_link}?hide_gdpr_banner=1&primary_color=ed8008`,
              })
            } catch (error) {
              // Silently handle Calendly errors
            }
            return false
          }

          // Add both click and touchend event listeners for better mobile support
          bookNowButton.addEventListener('click', handleBooking, { passive: false })
          bookNowButton.addEventListener('touchend', handleBooking, { passive: false })
        }

        // Add click handler for copy address button
        const copyAddressButton = content.querySelector('.showroom-booking__copy-address')
        if (copyAddressButton) {
          copyAddressButton.addEventListener('click', async (e) => {
            e.stopPropagation()
            const address = copyAddressButton.dataset.address
            const copyIcon = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z" /></svg>`
            try {
              if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(address)
              } else {
                // Fallback for older browsers or non-secure contexts
                const textArea = document.createElement('textarea')
                textArea.value = address
                textArea.style.position = 'fixed'
                textArea.style.left = '-999999px'
                textArea.style.top = '-999999px'
                document.body.appendChild(textArea)
                textArea.focus()
                textArea.select()
                document.execCommand('copy')
                textArea.remove()
              }

              // Visual feedback
              const originalHTML = copyAddressButton.innerHTML
              copyAddressButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>`
              copyAddressButton.classList.add('text-green-600')
              setTimeout(() => {
                copyAddressButton.innerHTML = originalHTML
                copyAddressButton.classList.remove('text-green-600')
              }, 2000)
            } catch (error) {
              // Visual feedback for error
              const originalHTML = copyAddressButton.innerHTML
              copyAddressButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>`
              copyAddressButton.classList.add('text-red-600')
              setTimeout(() => {
                copyAddressButton.innerHTML = originalHTML
                copyAddressButton.classList.remove('text-red-600')
              }, 2000)
            }
          })
        }

        div.appendChild(content)
        div.appendChild(pointer)
        this.div = div
        const panes = this.getPanes()
        panes.floatPane.appendChild(div)
      }

      draw () {
        const overlayProjection = this.getProjection()
        const position = overlayProjection.fromLatLngToDivPixel(this.position)
        const div = this.div
        if (position) {
          // Get actual dimensions of the info window
          const rect = div.getBoundingClientRect()
          const windowWidth = rect.width || 280 // fallback to min-width
          const windowHeight = rect.height || 160 // reasonable fallback

          // Center horizontally and position above the marker
          div.style.left = `${position.x - windowWidth / 2}px`
          div.style.top = `${position.y - windowHeight - 55}px` // 55px accounts for marker height + spacing
        }
      }

      onRemove () {
        if (this.div) {
          this.div.parentNode.removeChild(this.div)
          this.div = null
        }
      }
    }

    return CustomInfoWindow
  }

  toggleClearButton () {
    if (!this.clearButton) return
    if (this.postcodeInput.value) {
      this.clearButton.classList.remove('hidden')
      this.clearButton.classList.add('flex')
    } else {
      this.clearButton.classList.add('hidden')
      this.clearButton.classList.remove('flex')
    }
  }

  resetTool () {
    this.postcodeInput.value = ''
    this.toggleClearButton()
    this.searchResults = null
    this.initializeWithSortedShowrooms()
    const distanceElements = this.querySelectorAll('.showroom-booking__locations-column--distance')
    distanceElements.forEach(element => {
      element.textContent = ''
    })
    if (this.map) {
      this.resetMarkersToDefault()
      this.resetMapToUK()
    }
    this.hasZoomedToMarker = false
    if (this.closestShowroomsText) {
      this.closestShowroomsText.classList.add('hidden')
      this.closestShowroomsText.innerHTML = this.closestShowroomsText.innerHTML.replace(/<strong>[A-Z0-9\s]+<\/strong>/g, '{{ postcode }}')
    }
    this.loading = false
    this.removeAttribute('loading')
  }

  panToShowInfoWindow (markerPosition) {
    if (!this.map) return

    // Get map container height to calculate offset
    const mapDiv = this.map.getDiv()
    const mapHeight = mapDiv.offsetHeight
    const isMobile = window.innerWidth < 768

    // Calculate pixel offset based on additional info and device type
    let offsetPercentage = 0.15 // Default 15% of map height

    if (this.showAdditionalInfo) {
      // Increase offset when additional contact info is showing
      offsetPercentage = isMobile ? 0.35 : 0.25 // 35% on mobile, 25% on desktop
    } else {
      // Standard offset for basic info
      offsetPercentage = isMobile ? 0.25 : 0.15 // 25% on mobile, 15% on desktop
    }

    const pixelOffset = Math.floor(mapHeight * offsetPercentage)

    // Calculate the final center position by offsetting from marker position
    const bounds = this.map.getBounds()
    const projection = this.map.getProjection()

    if (bounds && projection) {
      // Convert current bounds to get the lat/lng per pixel ratio
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const latRange = ne.lat() - sw.lat()
      const latPerPixel = latRange / mapHeight

      // Calculate the offset in latitude (move north to position marker lower)
      const latOffset = (pixelOffset * latPerPixel)

      // Calculate final center position
      const finalCenter = {
        lat: markerPosition.lat + latOffset,
        lng: markerPosition.lng,
      }

      // Pan to the calculated position in one smooth motion
      this.map.panTo(finalCenter)
    } else {
      // Fallback to simple pan if projection/bounds not available
      this.map.panTo(markerPosition)
    }
  }

  zoomToClosestShowrooms (showrooms) {
    if (!showrooms || showrooms.length === 0 || !this.map) return
    const bounds = new google.maps.LatLngBounds()
    showrooms.forEach(showroom => {
      const position = {
        lat: parseFloat(showroom.pin_lat),
        lng: parseFloat(showroom.pin_long),
      }
      if (!isNaN(position.lat) && !isNaN(position.lng)) {
        bounds.extend(position)
      }
    })
    this.map.panToBounds(bounds, MAP_BOUNDS_PADDING)
    this.map.fitBounds(bounds, MAP_BOUNDS_PADDING)
    setTimeout(() => {
      const currentZoom = this.map.getZoom()
      let targetZoom = currentZoom
      if (this.searchInputType === 'address') {
        targetZoom = Math.max(1, currentZoom - 1)
      } else {
        targetZoom = Math.min(currentZoom, 12)
      }
      this.map.setZoom(targetZoom)
      this.hasZoomedToMarker = true
    }, 100)
    this.userChangedZoom = false
  }

  zoomToFilteredShowrooms (visibleShowrooms) {
    if (!this.map) return
    const bounds = new google.maps.LatLngBounds()
    let markerCount = 0
    this.showroomMarkers.forEach(marker => {
      if (marker.getMap() === this.map) {
        bounds.extend(marker.getPosition())
        markerCount++
      }
    })
    if (markerCount > 0) {
      setTimeout(() => {
        this.map.fitBounds(bounds, MAP_BOUNDS_PADDING)
        setTimeout(() => {
          const zoom = this.map.getZoom()
          if (zoom && zoom > 12) {
            this.map.setZoom(12)
          }
        }, 100)
      }, 100)
    }
  }

  debugShowroomData () {
    if (!window.showroomLocations || window.showroomLocations.length === 0) {
      return
    }
    const possibleIdProps = ['id', 'name', 'showroomId', 'showroom_id', 'closest_city']
    const uniqueProp = possibleIdProps.find(prop => window.showroomLocations[0][prop] !== undefined)
    if (uniqueProp) {
      this.showroomIdProperty = uniqueProp
    } else {
      this.showroomIdProperty = 'closest_city'
    }
  }

  decodeHtmlEntities (text) {
    if (!text) return ''
    const textArea = document.createElement('textarea')
    textArea.innerHTML = text
    return textArea.value
  }

  getCountryFlag (region) {
    const countryCode = this.getCountryCodeFromRegion(region)
    if (!countryCode) return ''
    const flag = countryCode
      .toUpperCase()
      .split('')
      .map(char => String.fromCodePoint(char.charCodeAt(0) + 127397))
      .join('')
    return flag
  }

  showAllMarkers () {
    this.showroomMarkers.forEach(marker => {
      marker.setMap(this.map)
    })
  }

  resetMarkersToDefault () {
    this.showAllMarkers()
  }

  ensureShowroomDataIntegrity (showroom) {
    // Ensure all required fields are present and properly formatted
    // Handle both snake_case and camelCase properties
    return {
      id: showroom.id || showroom.closestCity || showroom.closest_city || showroom.town || showroom.companyName || showroom.company_name,
      closestCity: showroom.closestCity || showroom.closest_city || '',
      town: showroom.town || '',
      companyName: showroom.companyName || showroom.company_name || '',
      streetAddress: showroom.streetAddress || showroom.street_address || '',
      postcode: showroom.postcode || '',
      phone: showroom.phone || '',
      email: showroom.email || '',
      website: showroom.website || '',
      websitePretty: showroom.websitePretty || showroom.website_pretty || showroom.website || '',
      region: showroom.region || '',
      regionName: showroom.regionName || showroom.region_name || '',
      regionHandle: showroom.regionHandle || showroom.region_handle || '',
      pinLat: showroom.pinLat || showroom.pin_lat || '',
      pinLong: showroom.pinLong || showroom.pin_long || '',
      distance: showroom.distance || 0,
      calendlyLink: showroom.calendlyLink || showroom.calendly_link || '',
    }
  }
}
if (!customElements.get('showroom-booking')) {
  customElements.define('showroom-booking', ShowroomBooking)
}
